import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Logo, TAGLINE } from "../components/Logo";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("expired")) {
      toast.info("Your session expired due to inactivity. Please sign in again.");
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}!`);
      nav(`/dashboard/${res.user.role}`);
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 ym-grid-bg opacity-30" />
        <Logo inverted />
        <div className="relative">
          <h2 className="font-heading font-black text-4xl leading-tight">
            Welcome back to<br />Young Minds.
          </h2>
          <p className="mt-4 text-blue-100 italic">"{TAGLINE}"</p>
        </div>
        <div className="relative text-blue-100 text-sm">© Young Minds Smart Learning Platform</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="lg:hidden mb-6"><Logo /></div>
        <div className="max-w-md w-full mx-auto">
          <h1 className="font-heading font-bold text-3xl text-slate-900">Sign in</h1>
          <p className="mt-2 text-slate-600">Enter your details to access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-semibold text-slate-700">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Password</label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                />
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60 transition-all"
            >
              {submitting ? "Signing in..." : "Sign in"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" data-testid="link-register" className="font-semibold text-blue-600 hover:underline">
              Create one
            </Link>
            <span className="mx-2 text-slate-300">·</span>
            <Link to="/forgot-password" data-testid="link-forgot-password" className="font-semibold text-blue-600 hover:underline">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
